<template>
  <div>
    <label for="cpfCnpjInput" class="form-label">CPF / CNPJ do Titular</label>
    <input
      id="cpfCnpjInput"
      type="text"
      class="form-control"
      :value="maskedValue"
      placeholder="CPF ou CNPJ do titular"
      maxlength="18"
      :readonly="readonly"
      :disabled="disabled"
      @input="handleInput"
      @blur="handleBlur"
      autocomplete="off"
    />
    <p class="form-text text-muted mt-1 small">
      <i class="fas fa-info-circle me-1"></i>
      {{ hint }}
    </p>
    <div v-if="error" class="text-danger mt-1">{{ error }}</div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  readonly:   { type: Boolean, default: false },
  disabled:   { type: Boolean, default: false },
  error:      { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue', 'type-detected']);

const maskedValue = ref('');

const detectedType = computed(() => {
  const digits = maskedValue.value.replace(/\D/g, '');
  if (digits.length === 11) return 'CPF';
  if (digits.length === 14) return 'CNPJ';
  return null;
});

const hint = computed(() => {
  if (detectedType.value === 'CPF')  return 'CPF detectado automaticamente.';
  if (detectedType.value === 'CNPJ') return 'CNPJ detectado automaticamente.';
  return 'Digite o CPF (11 dígitos) ou CNPJ (14 dígitos) do titular da conta.';
});

const formatCpfCnpj = (digits) => {
  const d = digits.slice(0, 14);
  if (d.length <= 11) {
    // CPF: 000.000.000-00
    let r = d;
    if (d.length > 3)  r = r.slice(0, 3) + '.' + r.slice(3);
    if (d.length > 6)  r = r.slice(0, 7) + '.' + r.slice(7);
    if (d.length > 9)  r = r.slice(0, 11) + '-' + r.slice(11);
    return r;
  } else {
    // CNPJ: 00.000.000/0000-00
    let r = d;
    if (d.length > 2)  r = r.slice(0, 2) + '.' + r.slice(2);
    if (d.length > 5)  r = r.slice(0, 6) + '.' + r.slice(6);
    if (d.length > 8)  r = r.slice(0, 10) + '/' + r.slice(10);
    if (d.length > 12) r = r.slice(0, 15) + '-' + r.slice(15);
    return r;
  }
};

const handleInput = (e) => {
  const digits = e.target.value.replace(/\D/g, '').slice(0, 14);
  maskedValue.value = formatCpfCnpj(digits);
  // Keep cursor at end after mask
  e.target.value = maskedValue.value;

  emit('update:modelValue', maskedValue.value);

  if (digits.length === 11)      emit('type-detected', 'CPF');
  else if (digits.length === 14) emit('type-detected', 'CNPJ');
  else                           emit('type-detected', null);
};

const handleBlur = () => {
  // Re-emit type on blur for late population
  const digits = maskedValue.value.replace(/\D/g, '');
  if (digits.length === 11)      emit('type-detected', 'CPF');
  else if (digits.length === 14) emit('type-detected', 'CNPJ');
  else                           emit('type-detected', null);
};

// Initialize / react to external modelValue changes (e.g., loading from store)
watch(() => props.modelValue, (val) => {
  const digits = (val || '').replace(/\D/g, '').slice(0, 14);
  const formatted = formatCpfCnpj(digits);
  if (formatted !== maskedValue.value) {
    maskedValue.value = formatted;
  }
}, { immediate: true });
</script>

<style scoped>
.form-label {
  font-weight: 600;
  color: #32355d;
  margin-bottom: 0.5rem;
  display: block;
}

.form-control {
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px solid #ced4da;
  font-size: 1rem;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.form-control:focus {
  border-color: #ff50a3;
  box-shadow: 0 0 0 0.25rem rgba(255, 80, 163, 0.25);
  outline: none;
}

.form-control[readonly],
.form-control:disabled {
  background-color: #eee;
  color: #999;
  font-style: italic;
  opacity: 0.8;
  cursor: not-allowed;
}

.text-danger {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.form-text {
  font-size: 0.82rem;
  color: #6c757d;
}
</style>

<style scoped>
.form-label {
  font-weight: 600;
  color: #32355d;
  margin-bottom: 0.5rem;
  display: block;
}

.form-control {
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px solid #ced4da;
  font-size: 1rem;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.form-control:focus {
  border-color: #ff50a3;
  box-shadow: 0 0 0 0.25rem rgba(255, 80, 163, 0.25);
  outline: none;
}

.form-control[readonly],
.form-control:disabled {
  background-color: #eee;
  color: #999;
  font-style: italic;
  opacity: 0.8;
  cursor: not-allowed;
}

.text-danger {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.form-text {
  font-size: 0.82rem;
  color: #6c757d;
}
</style>
